import { Badge } from '@/components/ui/badge';
import type { EpicDetail } from '@/lib/api/user-activity-details';
import { formatDistanceToNow } from 'date-fns';
import { Link } from 'react-router-dom';

interface EpicDetailListProps {
  items: EpicDetail[];
}

export function EpicDetailList({ items }: EpicDetailListProps) {
  return (
    <ul className="space-y-0" role="list">
      {items.map((item) => (
        <li key={item.id}>
          <EpicDetailItem item={item} />
        </li>
      ))}
    </ul>
  );
}

function EpicDetailItem({ item }: { item: EpicDetail }) {
  const ariaLabel = `Epic ${item.identifier}: ${item.name}. ${item.teamName ? `Team: ${item.teamName}.` : 'Personal scope.'} ${item.featureCount} feature${item.featureCount !== 1 ? 's' : ''}. ${item.creatorName ? `Created by ${item.creatorName}.` : ''} Created ${formatDistanceToNow(new Date(item.createdAt), { addSuffix: true })}`;

  return (
    <Link
      to={`/epics/${item.identifier}`}
      className="block border-b py-3 px-3 sm:px-4 hover:bg-muted/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
      aria-label={ariaLabel}
    >
      <div className="space-y-2">
        <div className="flex items-start sm:items-center gap-2 flex-col sm:flex-row">
          <Badge
            className="text-xs font-mono bg-indigo-900/30 text-indigo-300 px-2 rounded border-0 flex-shrink-0"
            aria-hidden="true"
          >
            {item.identifier}
          </Badge>
          <span className="font-medium text-sm sm:text-base line-clamp-2 sm:line-clamp-1 sm:truncate">{item.name}</span>
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-muted-foreground flex-wrap" aria-hidden="true">
          <span className="truncate max-w-[120px] sm:max-w-none">{item.teamName ?? 'Personal'}</span>
          <span className="hidden sm:inline">•</span>
          <span className="whitespace-nowrap">{item.featureCount} feature{item.featureCount !== 1 ? 's' : ''}</span>
          {item.creatorName && (
            <>
              <span className="hidden sm:inline">•</span>
              <span className="truncate max-w-[100px] sm:max-w-none">{item.creatorName}</span>
            </>
          )}
          <span className="hidden sm:inline">•</span>
          <span className="whitespace-nowrap">{formatDistanceToNow(new Date(item.createdAt), { addSuffix: true })}</span>
        </div>
      </div>
    </Link>
  );
}
